import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import styles from "./Header.module.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Modal from "../../Utils/Modal";
import ChangePass from "./ChangePass";
import { logout } from "../../Utils/Redux/UserSlice";
import { fetchCreditSummary } from "../../Utils/Redux/CreditSlice";
import useRazorpayPayment from '../RazorPay/paymentGateway';

import { wallet } from 'react-icons-kit/entypo/wallet'
import { cart } from 'react-icons-kit/entypo/cart'
import Icon from 'react-icons-kit';


import { formatAmount } from '../../Utils/helper';
import { iosCartOutline } from "react-icons-kit/ionicons/iosCartOutline"
import Notification from '../../pages/DashBoard/ExternalDash/SSE/Notification';
import { BASE_URL, LOGOUT_USER, USER_TOKEN } from '../../Utils/constant';
import useRefreshToken from '../../Hooks/usegenerateToken';
const MORE_LABEL = "More";
// Breathing room kept between the last visible link and the header controls.
const NAV_SAFETY_GAP = 12;

/**
 * Header navigation that always stays on a single row.
 *
 * A hidden copy of the full link list is kept in the DOM purely to measure the
 * natural width of every link. On mount and on every resize (browser zoom
 * counts as a resize, since it shrinks the CSS viewport) we work out how many
 * links fit in the space left between the logo and the right-hand controls and
 * push the rest into a "More" dropdown. That keeps the fixed header exactly one
 * row tall, which the pages rely on with their `margin-top: 5rem`.
 */
const HeaderNav = ({ list, active, boundaryRef }) => {
    const wrapRef = useRef(null);
    const measureRef = useRef(null);
    const moreRef = useRef(null);
    const moreBtnRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(list.length);
    const [moreOpen, setMoreOpen] = useState(false);
    const [menuPos, setMenuPos] = useState(null);

    const names = list.map((l) => l.name).join("|");

    useLayoutEffect(() => {
        const wrap = wrapRef.current;
        const measure = measureRef.current;
        if (!wrap || !measure) return undefined;

        const recalc = () => {
            const nodes = Array.from(measure.children);
            if (!nodes.length) return;

            const items = nodes.slice(0, -1);
            const moreWidth = nodes[nodes.length - 1].getBoundingClientRect().width;
            const gap = parseFloat(window.getComputedStyle(measure).columnGap) || 0;

            // Space we are allowed to fill. `clientWidth` is the flex box we were
            // given, but we also measure straight to the left edge of the
            // right-hand controls (cart / wallet / avatar) so a link can never
            // end up painted on top of them, whatever the flex box says.
            let available = wrap.clientWidth;
            const boundary = boundaryRef && boundaryRef.current;
            if (boundary) {
                const room =
                    boundary.getBoundingClientRect().left -
                    wrap.getBoundingClientRect().left -
                    NAV_SAFETY_GAP;
                if (room > 0 && room < available) available = room;
            }

            let used = 0;
            let count = 0;
            for (let i = 0; i < items.length; i++) {
                const width = items[i].getBoundingClientRect().width + (i > 0 ? gap : 0);
                if (used + width > available) break;
                used += width;
                count += 1;
            }

            // Anything left over needs a "More" trigger, which needs room too.
            if (count < items.length) {
                while (count > 0 && used + moreWidth + gap > available) {
                    count -= 1;
                    used -= items[count].getBoundingClientRect().width + (count > 0 ? gap : 0);
                }
            }

            setVisibleCount((prev) => (prev === count ? prev : count));
        };

        recalc();

        let observer;
        if (typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(recalc);
            observer.observe(wrap);
            observer.observe(measure);
            if (boundaryRef && boundaryRef.current) observer.observe(boundaryRef.current);
        }
        window.addEventListener("resize", recalc);
        // Web fonts land after first paint and change every link's width.
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(recalc).catch(() => {});
        }

        // The controls sit after us in the tree, so their ref is still empty
        // while this layout effect runs. Pick them up on the next frame.
        const frame = window.requestAnimationFrame(() => {
            if (observer && boundaryRef && boundaryRef.current) {
                observer.observe(boundaryRef.current);
            }
            recalc();
        });

        return () => {
            window.cancelAnimationFrame(frame);
            if (observer) observer.disconnect();
            window.removeEventListener("resize", recalc);
        };
    }, [names, boundaryRef]);

    useEffect(() => {
        if (!moreOpen) return undefined;
        const closeOnOutside = (event) => {
            if (moreRef.current && !moreRef.current.contains(event.target)) {
                setMoreOpen(false);
            }
        };
        const closeOnResize = () => setMoreOpen(false);
        document.addEventListener("mousedown", closeOnOutside);
        window.addEventListener("resize", closeOnResize);
        return () => {
            document.removeEventListener("mousedown", closeOnOutside);
            window.removeEventListener("resize", closeOnResize);
        };
    }, [moreOpen]);

    // The nav strip clips its overflow, so the dropdown is positioned as a fixed
    // element anchored to the trigger instead of living inside the clipped box.
    const toggleMore = () => {
        if (moreOpen) {
            setMoreOpen(false);
            return;
        }
        const rect = moreBtnRef.current && moreBtnRef.current.getBoundingClientRect();
        if (rect) {
            setMenuPos({
                top: rect.bottom + 8,
                right: Math.max(8, window.innerWidth - rect.right),
            });
        }
        setMoreOpen(true);
    };

    if (!list.length) return null;

    const visible = list.slice(0, visibleCount);
    const overflow = list.slice(visibleCount);
    const overflowHasActive = overflow.some((e) => e.link === active);

    return (
        <nav className={styles.hdrNavSection}>
            <div className={styles.hdrNavWrap} ref={wrapRef}>
                <ul ref={measureRef} className={[styles.hdrNavList, styles.hdrNavMeasure].join(" ")} aria-hidden="true">
                    {list.map((e) => (
                        <li className={styles.hdrNavItem} key={e.name}>
                            <span className={styles.hdrNavLink}>{e.name}</span>
                        </li>
                    ))}
                    <li className={styles.hdrNavItem}>
                        <span className={styles.hdrNavLink}>{MORE_LABEL}</span>
                    </li>
                </ul>

                <ul className={styles.hdrNavList}>
                    {visible.map((e) => (
                        <li className={styles.hdrNavItem} key={e.name}>
                            <Link
                                to={e.link}
                                className={[styles.hdrNavLink, active === e.link ? styles.hdrNavLinkActive : ""].join(" ")}
                            >
                                {e.name}
                            </Link>
                        </li>
                    ))}
                    {overflow.length > 0 && (
                        <li className={[styles.hdrNavItem, styles.hdrMoreWrap].join(" ")} ref={moreRef}>
                            <button
                                type="button"
                                ref={moreBtnRef}
                                aria-haspopup="true"
                                aria-expanded={moreOpen}
                                onClick={toggleMore}
                                className={[styles.hdrNavLink, overflowHasActive ? styles.hdrNavLinkActive : ""].join(" ")}
                            >
                                {MORE_LABEL}
                            </button>
                            {moreOpen && (
                                <div className={styles.hdrMoreMenu} style={menuPos || undefined}>
                                    {overflow.map((e) => (
                                        <Link
                                            key={e.name}
                                            to={e.link}
                                            onClick={() => setMoreOpen(false)}
                                            className={[styles.hdrNavLink, active === e.link ? styles.hdrNavLinkActive : ""].join(" ")}
                                        >
                                            {e.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </li>
                    )}
                </ul>
            </div>
        </nav>
    );
};

const Header = ({notifications}) => {
    const userData = useSelector(state => state.User)
    const cartData = useSelector(state => state.cart.items)
    const WalletData = useSelector(state => state.cart.wallet)
    const CreditData = useSelector(state => state.credit.data)
    const dispatch = useDispatch()
    const dropdownRef = useRef(null);
    const razorpay = useRazorpayPayment(userData.id)
    const location = useLocation()
    const [Popup, setPopup] = useState(false);
    const [menu, setMenu] = useState(false);
    const navigate = useNavigate()
    const params=useLocation()

    const User=useSelector(state=>state.User)
    const walletAmount=userData.user_type=="Verifier"?WalletData.balance:WalletData.credits
    
    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setMenu(false);
        }
    };

    // MENU

    const list = [
          // Users (for all user types)
        
          // Extra menu items only for Super Admin
          ...(userData.user_type === "Super Admin"
            ? [
                { name: "Dashboard", link: "/dashboard" }, 
                {name:"Users", link:"/users"},
                { name: "Templates", link: "/templates" },
                { name: "Forms", link: "/forms" },
                { name: "Appeals", link: "/appeals" },
                { name: "Payments", link: "/payments" },
                { name: "Requests", link: "/requests" },
                { name: "Insights", link: "/insights" },
                { name: "Add Employee", link: `/company/2/2/credentials` },
                { name: "Reports", link: "/reports"}
              ]
            : [])
        // { name: 'Help', link: '/help' }

    ]
    const [active, setactive] = useState("")

    useEffect(()=>{
        // setactive(list.find(l => location.pathname == l.link)?.link || '')
        const matched = list.find((l) => l.link === location.pathname);
  if (matched) {
    setactive(matched.link);
  } else {
    setactive(""); // no match, no tab active
  }


    },[location.pathname])

    useEffect(() => {
        if (userData.user_type === "Issuer") dispatch(fetchCreditSummary())
    }, [location.pathname])

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);

        };
    }, []);

    const logoutUser = () => {
        fetch(BASE_URL + LOGOUT_USER+"?"+"user_id="+User.user_id, {
              method: 'get',
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + User.access_token
              }
            })
            .then(res=>res.json())
            .then(data=>{
                dispatch(logout()),
                localStorage.clear(),
                sessionStorage.clear(),
                // setTimeout(()=>window.location.href = "/app/login",1000)
                window.location.href = "/login"
                }
            )
            .catch((err)=>{
                console.log(err);
                
            })
    }

    const OpenRazorpay = () => {
        razorpay()
    }

    const DropDown = () => {
        return (
            <div className={styles.dropdown}>
                <div className={styles.dropdownContent} >
                    <h1>{userData.email || userData.user_name}</h1>
                    {/* <span>{userData.email}</span> */}
                </div>
                <hr />
                {/* {
                    userData.user_type == "Verifier" &&
                    <div className={styles.changePass}>
                        <span onClick={OpenRazorpay}>Add Money</span>
                    </div>
                } */}
                <div className={styles.changePass}>
                    <span onClick={() => setPopup(true)}>Change Password</span>
                </div>
                {/* <hr style={{padding:"0",margin:"0"}}/> */}
                <div onClick={e => logoutUser()} className={styles.logout}>Logout</div>
            </div>
        )
    }





    const ShowControls = () => {
        if (
            (userData.user_type === "Verifier" ||
              (userData.user_type === "Issuer" && CreditData?.credit_enabled)) &&
            userData.user_role_name && params.pathname !== "/privacy-policy" && params.pathname!=="/contact-us" && params.pathname!=="/Term-and-Condition" && params.pathname!=="/Services"
          )
            return (
                <>
                    {userData.category!=="VISITOR"&& userData.user_type == "Verifier"&& <Link to={'/dashboard/cart'} className='link' style={{ display: 'flex' }}>
                    {cartData.length>0?<><Icon icon={cart} size={'1.5rem'} style={{ color: '#505965' }} />
                        <div className={styles.cartCount}>{cartData.length}</div>
                        </>:
                        <Icon icon={iosCartOutline} size={"2rem"}/>
                        }
                    </Link>}
                    {/* <img className={styles.helpandsupport} loading="eager" alt="" onClick={() => navigate('/dashboard/Help/')} src="/needhelplogo.svg" /> */}
                    <Link to={User.user_type=="Verifier"?'/dashboard/wallet':'/dashboard/credit'} className={styles.walletContainer} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }} title={userData.user_type=="Verifier"?(`${WalletData.currency==="USD"?"$":"₹"}${walletAmount}`):`${CreditData.total_credits} credits`}>
                        <Icon icon={wallet} size={'1.5rem'} style={{ color: '#505965' }} />
                        {userData.user_type === "Verifier" ?(`${WalletData.currency=="USD"?"$":"₹"}${formatAmount(walletAmount)}`): `${CreditData.total_credits} credits`}
                    </Link>
                </>

            )

    }


    const ShowWallet = () => {
        if (userData.user_type=="Verifier" && userData.category!=="VISITOR") {
            return (
                <div className={styles.profileContainer} >
                    < img className={[styles.avatarIcon, styles.imageContainer].join(" ")} loading="eager" alt="" onMouseOver={() => setMenu(true)} src="/usercircle.svg" />
                    {menu && <DropDown />}
                </div>

            )
        }
        else if(params.pathname!=="/privacy-policy" && params.pathname!=="/contact-us" && params.pathname!=="/Term-and-Condition") {
            return (
                <>
                    < img className={styles.avatarIcon} loading="eager" alt="" onMouseOver={() => setMenu(true)} src="/usercircle.svg" />
                    {menu && <DropDown />}
                </>
            )
        }

    }

    return (
        <>
            <header className={styles.headerStyle} >
                <div className={styles.hEADERTITLE}>
                    <Link to="/dashboard" className={styles.hdrLogoLink}>
                        <div className={styles.authnumberLogo}>
                            {/* <div className={styles.authnumberParent}>
                    <p className={styles.authnumberIcon}>AuthNumber</p>
                    <img className={styles.subtractIcon} loading="eager" alt="" src="/Header_icon.svg"/>
                </div> */}
                            <div className={styles.authnumberParent}>
                                <img className={styles.authnumberIcon} loading="eager" alt="" src="/authnumber_1.svg" />
                                <img className={styles.subtractIcon} loading="eager" alt="" src="/Header_icon.svg" />
                            </div>
                        </div>
                    </Link>
                    {(params.pathname !== "/privacy-policy" && params.pathname !== "/contact-us" && params.pathname !== "/app/Term-and-Condition" && (userData.user_type === "internal_user" || userData.user_type === "Super Admin")) && <HeaderNav list={list} active={active} boundaryRef={dropdownRef} />}
                    <div className={styles.appsIconParent} ref={dropdownRef}>
                    {params.pathname !== "/privacy-policy" && params.pathname!=="/contact-us" && params.pathname!=="/Term-and-Condition" && params.pathname!=="/Services" &&(userData.user_type === "Issuer" || userData.user_type === "Verifier") && (<Notification notifications={notifications} />)}
                        <ShowControls />
                        <ShowWallet />
                    </div>
                </div>
            </header>
            <Modal title={"Change Password"} isOpen={Popup} onClose={setPopup} blockOutsideClick={true}>
                <ChangePass setPopup={setPopup} />
            </Modal>
        </>
    )
}
export default Header;